import { Buyer, DealRequest } from '@/app/api/types';
import Step1Icon from '@/app/assets/icons/chronology/step-1.svg?react';
import Step2Icon from '@/app/assets/icons/chronology/step-2.svg?react';
import Step3Icon from '@/app/assets/icons/chronology/step-3.svg?react';
import Step4Icon from '@/app/assets/icons/chronology/step-4.svg?react';
import Step5Icon from '@/app/assets/icons/chronology/step-5.svg?react';
import { Actor, ChronologySigner, ChronologyStep } from '../types';
import { getSignerActors } from './get-signer-actors';
import { TFunction } from 'i18next';

const formatDate = (date?: string | null) => date?.replace(' ', ', ') ?? '';

const withDate = (text: string, date?: string | null) =>
  date ? `${text} - ${formatDate(date)}` : text;

const REJECTED_ACTION = 'rejected';

const SIGNING_STEP_INDEX = 2;

export const CHRONOLOGY_STEPS_COUNT = 5;

const SIGNER_LABELS: Record<Actor, string> = {
  buyer: 'buyer',
  seller: 'supplier',
  factor: 'factor',
};

export const getChronologySteps = (
  row: DealRequest,
  t: TFunction<'translation', undefined>,
  viewerTin?: string
): ChronologyStep[] => {
  const companies: Record<Actor, Buyer | undefined> = {
    buyer: row.buyer,
    seller: row.seller,
    factor: row.factor,
  };

  const getName = (actor: Actor) =>
    companies[actor]?.shortName || companies[actor]?.name || '';

  const sellerName = getName('seller');
  const factorName = getName('factor');

  const contractSigners = row.factoringContract?.signers ?? [];
  const registrySigners = row.debtRegistry?.signers ?? [];

  /**
   * Договор идёт первым: если сторона подписала и договор, и реестр,
   * показываем время подписи договора. Реестр остаётся запасным источником -
   * в реверсивном факторинге поставщика в договоре нет вовсе, его подпись
   * есть только там.
   */
  const signers = [...contractSigners, ...registrySigners];

  const findSigner = (actor: Actor) => {
    const actorSigners = signers.filter((signer) => signer.actor === actor);
    return (
      actorSigners.find((signer) => signer.action === REJECTED_ACTION) ??
      actorSigners.find((signer) => !!signer.signedAt) ??
      actorSigners[0]
    );
  };

  const signerActors = getSignerActors(
    row,
    signers.map((signer) => signer.actor)
  );

  const rejectedActor = signerActors.find(
    (actor) => findSigner(actor)?.action === REJECTED_ACTION
  );

  /** После отклонения подписывать уже некому — очереди нет */
  const pendingActor = rejectedActor
    ? undefined
    : signerActors.find((actor) => !findSigner(actor)?.signedAt);

  const getSignerStatus = (actor: Actor) => {
    const signer = findSigner(actor);

    if (signer?.action === REJECTED_ACTION) {
      return {
        label: t('chronology.rejected'),
        date: signer.signedAt,
        isRejected: true,
      };
    }
    if (signer?.signedAt) {
      return { label: t('chronology.signed'), date: signer.signedAt };
    }
    // Одна из сторон отклонила — остальным подписывать уже не нужно
    if (rejectedActor) {
      return { label: t('chronology.not_required') };
    }
    if (
      actor === pendingActor &&
      !!viewerTin &&
      companies[actor]?.tin === viewerTin
    ) {
      return { label: t('waiting_for_your_sign'), isViewerTurn: true };
    }
    return undefined;
  };

  const stepSigners: ChronologySigner[] = signerActors.map((actor) => ({
    label: t(SIGNER_LABELS[actor]),
    name: getName(actor),
    status: getSignerStatus(actor),
  }));

  const isDeclinedByFactor =
    !!row.factorDeclinedAt || !!row.factorDeclineReason;

  const steps: ChronologyStep[] = [
    {
      id: 0,
      icon: <Step1Icon />,
      label: t('chronology.flow_labels.0'),
      caption: withDate(sellerName, row.createdAt),
    },
    {
      id: 1,
      icon: <Step2Icon />,
      label: t('chronology.flow_labels.1'),
      caption: withDate(factorName, row.factorAcceptedAt),
      currentCaption: t('chronology.waiting_factor_approve', {
        name: factorName,
      }),
      rejectedCaption: isDeclinedByFactor
        ? withDate(
            t('chronology.rejected_by', { name: factorName }),
            row.factorDeclinedAt
          )
        : undefined,
      rejectionReason: isDeclinedByFactor
        ? (row.factorDeclineReason ?? undefined)
        : undefined,
    },
    {
      id: 2,
      icon: <Step3Icon />,
      label: t('chronology.flow_labels.2'),
      caption: withDate(
        t('chronology.signed_by_actor'),
        row.factoringContractDate
      ),
      currentCaption: t('chronology.waiting_prev_step'),
      rejectedCaption: rejectedActor
        ? t('chronology.rejected_by', {
            name: getName(rejectedActor),
          })
        : undefined,
      signers: stepSigners.length ? stepSigners : undefined,
    },
    {
      id: 3,
      icon: <Step4Icon />,
      label: t('chronology.flow_labels.3'),
      caption: withDate(t('chronology.flow_labels.3'), row.payoutAt),
    },
    {
      id: 4,
      icon: <Step5Icon />,
      label: t('chronology.flow_labels.4'),
      caption: withDate(t('chronology.closed'), row.repaymentAt),
    },
  ];

  return rejectedActor ? steps.slice(0, SIGNING_STEP_INDEX + 1) : steps;
};
